import { Section } from '@common/components/BookingForms';
import { Spinner } from '@common/components/Spinner/Spinner';
import { useGetSpaPackage, useGetSpaPackageConfigGroups } from '@common/features/spa';
import { useGetSpaExperiences } from '@common/features/spa/hooks/useGetSpaExperiences';
import { useIsWindowWiderThan } from '@common/hooks';
import { useTranslations } from '@lang';
import { gridTheme } from '@utils/grid';
import { some } from '@utils/some';
import moment from 'moment';
import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { SpaEntityType } from 'shared-types/Spa';
import { PageSubtitle } from '../../FormSteps/FormSteps.styles';
import { getSpaOptionText } from '../../helpers/getSpaOptionText';
import { FormStep, PackagesFormValues } from '../../types';
import { ExperiencesGroupOptions } from './components/ExperiencesGroupOptions';
import { ExperiencesGroupOptionsMobile } from './components/ExperiencesGroupOptionsMobile';
import { SelectNumberText, SpaTitleWrapper } from './ExperiencesGroup.styles';
import * as Sentry from '@sentry/nextjs';
import { usePackageConfiguration } from '../../contexts';

interface Props {
  selectionGroup: Omit<FormStep, 'step' | 'type'>;
  allOptionsIncluded?: boolean;
  dayNumber: number;
}
export const ExperiencesGroup: FC<Props> = ({ selectionGroup, allOptionsIncluded, dayNumber }) => {
  const { t } = useTranslations();
  const isMobile = !useIsWindowWiderThan(gridTheme.breakpoints.md);
  const { watch, setValue } = useFormContext<PackagesFormValues, any>();
  const selectedExperiences = watch('selectedExperiences');

  const { property, journeyParams, spaPackageId, spaOpenTime, spaCloseTime, isPackageRepeating } =
    usePackageConfiguration();

  const {
    treatments = [],
    facilities = [],
    classes = [],
    isFetching,
  } = useGetSpaExperiences(
    property.id,
    property.defaultLanguage,
    selectionGroup.options?.map((option) => ({
      id: String(option.id),
      type: option.experienceType ?? 'spa-facilities',
      name: option.name,
    })),
    null
  );
  const totalGuests = journeyParams.adults + journeyParams.children;

  const { data: spaDay, isFetching: isSpaDayFetching } = useGetSpaPackage(
    property.id,
    Number(spaPackageId),
    property.defaultLanguage,
    true
  );

  const {
    data: spaPackageConfigGroups,
    isFetching: isSpaPackageConfigGroupsFetching,
    isLoading: isSpaPackageConfigGroupsLoading,
  } = useGetSpaPackageConfigGroups(
    spaDay?.configurationGroups,
    property.id,
    Number(spaPackageId),
    moment(journeyParams.fromDate).add(dayNumber, 'days').format('YYYY-MM-DD'),
    totalGuests,
    spaOpenTime,
    spaCloseTime
  );
  const isSpaPackageConfigGroupsReady =
    !isSpaPackageConfigGroupsLoading || !isSpaPackageConfigGroupsFetching;

  const experienceTimeslotsMap = new Map(
    spaPackageConfigGroups.flatMap((group) =>
      group.options
        .filter((option) => some(option.timeslots))
        .map((option) => [option.id, option.timeslots])
    )
  );

  const spaExperiencesWithClasses: Partial<SpaEntityType>[] = [
    ...treatments,
    ...facilities,
    ...classes.map((cl) => {
      const selectedId = selectionGroup?.options?.find(
        (option) => option.id === Number(cl.id)
      )?.optionId;
      return {
        ...cl,
        timeslots: selectedId ? experienceTimeslotsMap.get(selectedId) : [],
      };
    }),
  ];

  const hasAvailableTimeslots = some(spaExperiencesWithClasses) && experienceTimeslotsMap.size > 0;
  spaExperiencesWithClasses?.forEach((experience) => {
    if (!hasAvailableTimeslots) {
      experience.unAvailable = true;
      return;
    }

    const selectedId = selectionGroup.options?.find(
      (option) => option.id === Number(experience.id)
    );
    const timeslots = selectedId ? experienceTimeslotsMap.get(selectedId.optionId) : undefined;
    experience.unAvailable = !some(timeslots);
  });

  const allOptionsUnavailable = spaExperiencesWithClasses.every(
    (experience) => experience.unAvailable
  );

  // Only report if we've actually loaded data and still have no availability
  const hasLoadedConfigGroups =
    !isSpaPackageConfigGroupsLoading &&
    !isSpaPackageConfigGroupsFetching &&
    spaPackageConfigGroups.length > 0;

  if (allOptionsUnavailable && hasLoadedConfigGroups) {
    Sentry.captureEvent({
      message:
        'All spa options are unavailable for overnight package despite surpassing inital availability check',
      tags: {
        propertyId: property.id,
        spaPackageId,
        dayNumber,
        fromDate: moment(journeyParams.fromDate).format('YYYY-MM-DD'),
        toDate: moment(journeyParams.toDate).format('YYYY-MM-DD'),
        configGroupsWithTimeslots: JSON.stringify(spaPackageConfigGroups),
      },
    });
  }

  function plusMinusSpaOption(
    plusMinus: 'plus' | 'minus',
    id: number,
    optionId: number,
    startTime?: string
  ) {
    const spaOption = (selectionGroup.options || []).find(
      (option) => option.id === id && option.optionId === optionId
    );
    const { delayAfter, optionGroupId } = selectionGroup;

    if (!spaOption || !selectedExperiences) return;

    const existingExperience = isPackageRepeating
      ? selectedExperiences.find(
          (exp) => exp.id === id && exp.optionId === optionId && exp.dayNumber === dayNumber
        )
      : selectedExperiences.find((exp) => exp.id === id && exp.optionId === optionId);
    const otherExperiences = isPackageRepeating
      ? selectedExperiences.filter(
          (exp) => exp.id !== id || exp.optionId !== optionId || exp.dayNumber !== dayNumber
        )
      : selectedExperiences.filter(
          (exp) => exp.id !== id || exp.optionId !== optionId || exp.startTime !== startTime
        );

    const baseExperience = {
      dayNumber: Number(dayNumber),
      delayAfter,
      type: spaOption.experienceType,
      optionGroupId,
      id: spaOption.id,
      optionId: spaOption.optionId,
      isIncluded: false,
      numberOfGroupSelectionsRequired: selectionGroup.selectionsRequired || 1,
    };

    if (plusMinus === 'plus') {
      const newCount = (existingExperience?.count || 0) + 1;
      return setValue('selectedExperiences', [
        ...otherExperiences,
        { ...baseExperience, count: newCount },
      ]);
    }

    if (!existingExperience?.count) {
      return setValue('selectedExperiences', otherExperiences);
    }

    if (existingExperience.count === 1) {
      return setValue('selectedExperiences', otherExperiences);
    }

    return setValue('selectedExperiences', [
      ...otherExperiences,
      { ...baseExperience, count: existingExperience.count - 1 },
    ]);
  }

  return (
    <>
      {allOptionsIncluded ? (
        <SpaTitleWrapper data-cy="page-title-description">
          <SelectNumberText data-cy="selection-number-text">
            {t('spa.allOptionsIncluded')}
          </SelectNumberText>
        </SpaTitleWrapper>
      ) : (
        <>
          <SpaTitleWrapper data-cy="page-title-description">
            <PageSubtitle data-cy="page-title">
              {moment(journeyParams.fromDate).add(dayNumber, 'days').format('DD MMM YYYY')}
            </PageSubtitle>
            <SelectNumberText data-cy="selection-number-text">
              {t('spa.selectOption', {
                optionType: getSpaOptionText(selectionGroup, totalGuests),
                selectionAmount: (selectionGroup.selectionsRequired || 1) * totalGuests,
                selectionAmountPerGuest: selectionGroup.selectionsRequired || 1,
              })}
            </SelectNumberText>
          </SpaTitleWrapper>
        </>
      )}
      {isFetching || isSpaDayFetching || !isSpaPackageConfigGroupsReady ? (
        <Spinner
          showDelayMessage
          isActive={
            (isFetching && !spaExperiencesWithClasses) ||
            isSpaDayFetching ||
            !isSpaPackageConfigGroupsReady
          }
        />
      ) : isMobile ? (
        <ExperiencesGroupOptionsMobile
          selectionGroup={selectionGroup}
          allOptionsIncluded={allOptionsIncluded}
          spaExperiences={spaExperiencesWithClasses ?? []}
          plusMinusSpaOption={plusMinusSpaOption}
          totalGuests={totalGuests}
          dayNumber={dayNumber}
        />
      ) : (
        <Section>
          <ExperiencesGroupOptions
            selectionGroup={selectionGroup}
            allOptionsIncluded={allOptionsIncluded}
            spaExperiences={spaExperiencesWithClasses ?? []}
            plusMinusSpaOption={plusMinusSpaOption}
            totalGuests={totalGuests}
            dayNumber={dayNumber}
          />
        </Section>
      )}
    </>
  );
};
